import { useId, useRef, useState } from "react";
import { AudioLines, Check, ChevronsUpDown, CircleAlert, Play, Search, Square } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import {
  VOICE_GENDER_FILTERS,
  VOICE_GENDER_FILTER_LABELS,
  VOICE_GENDER_LABELS,
  findVoices,
  geminiVoiceOf,
  type GeminiVoice,
  type VoiceGenderFilter,
} from "@/constants/geminiVoices";
import { useVoiceSample, type VoiceSample } from "./useVoiceSample";

interface VoiceSelectorProps {
  /** The button's id, for the `<Label htmlFor>` that names it. */
  id?: string;
  value: string;
  onValueChange: (value: string) => void;
}

/** "Female · Bright", the dot left unread and the words apart. */
function Traits({ voice }: { voice: GeminiVoice }) {
  return (
    <>
      {VOICE_GENDER_LABELS[voice.gender]} <span aria-hidden="true">·</span> {voice.style}
    </>
  );
}

/**
 * The agent's voice (`config.voice.voiceId`): a button showing it, which opens a dialog of all 30 voices with their
 * gender, style and a sample of each, a search and a gender filter (owner, 2026-11-02). A voice is picked, then used;
 * Cancel, Escape or closing keeps the voice as it was, and focus goes back to the button.
 */
export function VoiceSelector({ id, value, onValueChange }: VoiceSelectorProps) {
  const [open, setOpen] = useState(false);
  const sample = useVoiceSample();
  const summaryId = useId();
  const optionIds = useId();
  const voice = geminiVoiceOf(value);

  const close = () => {
    sample.stop();
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? setOpen(true) : close())}>
      <DialogTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          aria-describedby={`${summaryId}name ${summaryId}traits`}
          className="h-auto w-full justify-between gap-3 px-3 py-2 text-left font-normal"
        >
          <span className="flex min-w-0 items-center gap-3">
            <span
              aria-hidden="true"
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary"
            >
              <AudioLines />
            </span>
            <span className="min-w-0">
              <span id={`${summaryId}name`} className="block truncate text-sm font-medium">
                {voice?.id ?? (value || "Choose a voice")}
              </span>
              {voice && (
                <span id={`${summaryId}traits`} className="block truncate text-xs text-foreground/70">
                  <Traits voice={voice} />
                </span>
              )}
            </span>
          </span>
          <ChevronsUpDown aria-hidden="true" className="opacity-50" />
        </Button>
      </DialogTrigger>
      <DialogContent
        className="flex max-h-[calc(100dvh-2rem)] w-[calc(100vw-2rem)] max-w-3xl flex-col gap-4 overflow-y-auto rounded-lg bg-card p-4 sm:max-h-[min(85dvh,46rem)] sm:p-6"
        // The search keeps focus; the voice in use scrolls into sight.
        onOpenAutoFocus={() => document.getElementById(`${optionIds}${voice?.id}`)?.scrollIntoView({ block: "nearest" })}
      >
        <VoicePicker
          value={voice}
          optionIds={optionIds}
          sample={sample}
          onUse={(picked) => {
            onValueChange(picked);
            close();
          }}
          onCancel={close}
        />
      </DialogContent>
    </Dialog>
  );
}

interface VoicePickerProps {
  /** The voice in use; undefined when the agent's is none of the 30. */
  value: GeminiVoice | undefined;
  /** Each voice's row id is this followed by its name. */
  optionIds: string;
  sample: VoiceSample;
  onUse: (voiceId: string) => void;
  onCancel: () => void;
}

/** The dialog's body: it mounts on each open, so the search, the filter and the pick start afresh. */
function VoicePicker({ value, optionIds, sample, onUse, onCancel }: VoicePickerProps) {
  const ids = useId();
  const searchBox = useRef<HTMLInputElement>(null);
  const [picked, setPicked] = useState(value);
  const [search, setSearch] = useState("");
  const [gender, setGender] = useState<VoiceGenderFilter>("all");
  const voices = findVoices(search, gender);

  // The button goes as the voices come back: focus goes to the search instead of being lost.
  const showAll = () => {
    setSearch("");
    setGender("all");
    searchBox.current!.focus();
  };

  return (
    <>
      <DialogHeader className="pr-8 text-left">
        <DialogTitle>Choose a voice</DialogTitle>
        <DialogDescription>
          Play a sample, then pick the voice your agent speaks with. Every voice speaks any language, with the accent
          you set under Accent.
        </DialogDescription>
      </DialogHeader>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative min-w-0 flex-1">
          <Search
            aria-hidden="true"
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
          />
          <Input
            ref={searchBox}
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search by name or style"
            aria-label="Search voices"
            autoComplete="off"
            className="pl-9"
          />
        </div>
        <fieldset className="flex shrink-0 gap-1 rounded-md border p-1">
          <legend className="sr-only">Gender</legend>
          {VOICE_GENDER_FILTERS.map((option) => {
            const checked = gender === option;
            return (
              <label
                key={option}
                className={cn(
                  "flex flex-1 cursor-pointer items-center justify-center gap-1.5 rounded px-2 py-1 text-sm font-medium transition-colors sm:px-3",
                  "has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring has-[:focus-visible]:ring-offset-1",
                  // Beside each shadow ring, a transparent outline: Windows' high-contrast mode drops shadows and paints it.
                  "has-[:focus-visible]:outline has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-transparent",
                  checked
                    ? "bg-primary text-primary-foreground forced-colors:outline forced-colors:outline-2"
                    : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
                )}
              >
                <input
                  type="radio"
                  name={`${ids}gender`}
                  value={option}
                  checked={checked}
                  onChange={() => setGender(option)}
                  className="sr-only"
                />
                {VOICE_GENDER_FILTER_LABELS[option]}
                <span className="text-xs tabular-nums">{findVoices(search, option).length}</span>
              </label>
            );
          })}
        </fieldset>
      </div>

      <p role="status" className="sr-only">
        {voices.length === 1 ? "1 voice" : `${voices.length} voices`}
      </p>
      {sample.failed && (
        <p role="alert" className="flex items-center gap-2 text-sm text-foreground">
          <CircleAlert aria-hidden="true" className="h-4 w-4 shrink-0 text-destructive" />
          Couldn't play the sample of {sample.failed}. Try again.
        </p>
      )}

      <div className="-mx-4 min-h-40 flex-1 overflow-y-auto border-y px-4 py-4 sm:-mx-6 sm:px-6">
        {voices.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-12 text-center">
            <p className="text-sm text-muted-foreground">No voice matches “{search.trim()}”.</p>
            <Button type="button" variant="outline" size="sm" onClick={showAll}>
              Show all voices
            </Button>
          </div>
        ) : (
          <div role="radiogroup" aria-label="Voices" className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {voices.map((voice) => (
              <VoiceOption
                key={voice.id}
                id={`${optionIds}${voice.id}`}
                name={`${ids}voice`}
                voice={voice}
                selected={voice.id === picked?.id}
                playing={sample.playing === voice.id}
                onPick={() => setPicked(voice)}
                onUse={() => onUse(voice.id)}
                onToggleSample={() => sample.toggle(voice.id)}
              />
            ))}
          </div>
        )}
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <p className="min-w-0 flex-1 truncate text-sm text-muted-foreground">
          {picked ? (
            <>
              <span className="font-medium text-foreground">{picked.id}</span> <span aria-hidden="true">·</span>{" "}
              <Traits voice={picked} />
            </>
          ) : (
            "No voice picked"
          )}
        </p>
        <div className="flex gap-2">
          <Button type="button" variant="outline" className="flex-1 sm:flex-none" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="button" className="flex-1 sm:flex-none" disabled={!picked} onClick={() => onUse(picked!.id)}>
            {picked ? `Use ${picked.id}` : "Use voice"}
          </Button>
        </div>
      </div>
    </>
  );
}

interface VoiceOptionProps {
  id: string;
  /** The radio group's name. */
  name: string;
  voice: GeminiVoice;
  selected: boolean;
  playing: boolean;
  onPick: () => void;
  /** Enter on the voice uses it at once. */
  onUse: () => void;
  onToggleSample: () => void;
}

/** One voice: its sample's play button, and a radio (the whole tile) named by the voice, its gender and style. */
function VoiceOption({ id, name, voice, selected, playing, onPick, onUse, onToggleSample }: VoiceOptionProps) {
  return (
    <div
      id={id}
      className={cn(
        "relative flex items-center gap-3 rounded-lg border p-3 transition-colors",
        "has-[input:focus-visible]:ring-2 has-[input:focus-visible]:ring-ring has-[input:focus-visible]:ring-offset-2 has-[input:focus-visible]:ring-offset-background",
        "has-[input:focus-visible]:outline has-[input:focus-visible]:outline-2 has-[input:focus-visible]:outline-offset-2 has-[input:focus-visible]:outline-transparent",
        selected ? "border-primary bg-primary/5" : "hover:border-primary/40 hover:bg-accent/50",
      )}
    >
      <button
        type="button"
        onClick={onToggleSample}
        aria-label={playing ? `Stop the sample of ${voice.id}` : `Play a sample of ${voice.id}`}
        className={cn(
          "relative z-10 flex h-9 w-9 shrink-0 items-center justify-center rounded-full border transition-colors",
          "focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
          "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-transparent",
          playing
            ? "border-primary bg-primary text-primary-foreground"
            : "bg-background text-foreground hover:border-primary hover:text-primary",
        )}
      >
        {playing ? <Square className="h-3 w-3 fill-current" /> : <Play className="ml-0.5 h-3.5 w-3.5 fill-current" />}
      </button>
      <label className="flex min-w-0 flex-1 cursor-pointer items-center gap-2 after:absolute after:inset-0 after:rounded-lg">
        <input
          type="radio"
          name={name}
          value={voice.id}
          checked={selected}
          onChange={onPick}
          onKeyDown={(event) => event.key === "Enter" && onUse()}
          aria-labelledby={`${id}-name`}
          aria-describedby={`${id}-traits`}
          className="sr-only"
        />
        <span className="min-w-0 flex-1">
          <span id={`${id}-name`} className="block truncate text-sm font-medium">
            {voice.id}
          </span>
          <span id={`${id}-traits`} className="block truncate text-xs text-foreground/70">
            <Traits voice={voice} />
          </span>
        </span>
        <Check aria-hidden="true" className={cn("h-4 w-4 shrink-0 text-primary", !selected && "invisible")} />
      </label>
    </div>
  );
}
